import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { Search, Filter, Eye, Calendar, DollarSign, Users, RefreshCw, Grid, List, Plus, Settings } from 'lucide-react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import LoadingSpinner from '../ui/LoadingSpinner';
import './CuratorSubmissions.css';

const CuratorSubmissions = () => {
  const { selectedOrganization } = useOutletContext() || {};
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    upcoming: 0,
    past: 0,
    totalSubmissions: 0
  });

  const navigate = useNavigate();

  // Helper function to detect and render HTML content
  const renderHTMLContent = (content, className = 'html-content') => {
    if (!content) return null;
    
    // Check if the content contains HTML tags
    const htmlRegex = /<[^>]*>/;
    const isHTML = htmlRegex.test(content);
    
    if (isHTML) {
      return (
        <div 
          className={className} 
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    } else {
      return content;
    }
  };

  // Helper function to truncate content with HTML support
  const truncateContent = (content, maxLength = 150) => {
    if (!content) return '';
    
    // Check if content contains HTML
    const htmlRegex = /<[^>]*>/;
    const isHTML = htmlRegex.test(content);
    
    if (isHTML) {
      // For HTML content, remove tags for length calculation
      const textOnly = content.replace(/<[^>]*>/g, '');
      if (textOnly.length <= maxLength) {
        return content; // Return full HTML if short enough
      }
      // For longer HTML content, provide a truncated plain text version
      return textOnly.substring(0, maxLength) + '...';
    } else {
      // Original logic for plain text
      return content.length <= maxLength ? content : content.substring(0, maxLength) + '...';
    }
  };

  // Get current user
  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  };

  // Load submissions for curator's organization
  const loadCuratorSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = await getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // If no organization selected, show empty state
      if (!selectedOrganization?.organization_id) {
        setSubmissions([]);
        setStats({ total: 0, active: 0, upcoming: 0, past: 0, totalSubmissions: 0 });
        return;
      }

      // Get collections for the selected organization
      const { data: collections, error: collectionsError } = await supabase
        .from('collections')
        .select('collection_id')
        .eq('organization_id', selectedOrganization.organization_id);

      if (collectionsError) {
        console.warn('Error fetching collections:', collectionsError);
      }

      const collectionIds = collections?.map(c => c.collection_id) || [];
      console.log('Collections for organization:', collectionIds);

      if (collectionIds.length === 0) {
        setSubmissions([]);
        setStats({ total: 0, active: 0, upcoming: 0, past: 0, totalSubmissions: 0 });
        setLoading(false);
        return;
      }

      // Build query for submissions linked to organization's collections
      let submissionsQuery = supabase
        .from('submissions_details')
        .select(`
          submission_id,
          title,
          theme,
          callout_headline,
          submission_deadline,
          submission_price,
          max_allowed,
          featured,
          member_free,
          created_at,
          collection_id,
          gallery_id,
          galleries!gallery_id (
            name,
            address,
            type
          ),
          collections!collection_id (
            title,
            description
          )
        `)
        .in('collection_id', collectionIds)
        .order('created_at', { ascending: false });

      // Add search filter
      if (searchQuery.trim()) {
        submissionsQuery = submissionsQuery.or(`title.ilike.%${searchQuery}%,theme.ilike.%${searchQuery}%`);
      }

      const { data: submissionsData, error: submissionsError } = await submissionsQuery;

      if (submissionsError) throw submissionsError;

      console.log('Curator submissions data:', submissionsData);

      // Process submissions and calculate stats
      const now = new Date();
      const processedSubmissions = submissionsData?.map(submission => {
        const deadline = submission.submission_deadline ? new Date(submission.submission_deadline) : null;
        const isActive = deadline ? deadline >= now : false;
        const isPast = deadline ? deadline < now : false;
        
        return {
          ...submission,
          status: deadline 
            ? (isActive ? 'active' : 'past')
            : 'no-deadline',
          deadlineDate: deadline,
          galleryName: submission.galleries?.name || 'Unknown Gallery',
          collectionTitle: submission.collections?.title || null
        };
      }) || [];

      // Filter by status if needed
      const filteredSubmissions = statusFilter === 'all' 
        ? processedSubmissions 
        : processedSubmissions.filter(submission => submission.status === statusFilter);

      setSubmissions(filteredSubmissions);

      // Calculate stats
      const totalSubmissions = processedSubmissions.length;
      const activeSubmissions = processedSubmissions.filter(s => s.status === 'active').length;
      const upcomingSubmissions = activeSubmissions; // Active submissions are upcoming
      const pastSubmissions = processedSubmissions.filter(s => s.status === 'past').length;

      setStats({
        total: totalSubmissions,
        active: activeSubmissions,
        upcoming: upcomingSubmissions,
        past: pastSubmissions,
        totalSubmissions
      });

    } catch (error) {
      console.error('Error loading curator submissions:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('CuratorSubmissions: selectedOrganization changed:', selectedOrganization?.organization_id);
    loadCuratorSubmissions();
  }, [searchQuery, statusFilter, selectedOrganization?.organization_id]);

  const handleSubmissionClick = (submissionId) => {
    // Find the submission to get its collection_id
    const submission = submissions.find(s => s.submission_id === submissionId);
    if (submission && submission.collection_id) {
      navigate(`/curator/collections/${submission.collection_id}`);
    } else {
      console.error('Collection ID not found for submission:', submissionId);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'Free';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No deadline';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'past': return 'status-past';
      default: return 'status-neutral';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'past': return 'Past';
      default: return 'No Deadline';
    }
  };

  if (loading) {
    return (
      <div className="curator-submissions">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="curator-submissions">
        <div className="error-message">
          <p>Error loading submissions: {error}</p>
          <button onClick={loadCuratorSubmissions} className="retry-button">
            <RefreshCw size={16} color="#ffffff" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-submissions">
      <div className="submissions-header">
        <div className="header-content">
          <h1>Submissions</h1>
          <p>Manage submission opportunities from your galleries</p>
        </div>
        
        {/* Stats in header */}
        <div className="header-stats">
          <div className="mini-stat">
            <div className="mini-stat-icon total">
              <DollarSign size={18} />
            </div>
            <div className="mini-stat-content">
              <span className="mini-stat-number">{stats.total}</span>
              <span className="mini-stat-label">Total</span>
            </div>
          </div>
          
          <div className="mini-stat">
            <div className="mini-stat-icon active">
              <Calendar size={18} />
            </div>
            <div className="mini-stat-content">
              <span className="mini-stat-number">{stats.active}</span>
              <span className="mini-stat-label">Active</span>
            </div>
          </div>
          
          <div className="mini-stat">
            <div className="mini-stat-icon past">
              <Users size={18} />
            </div>
            <div className="mini-stat-content">
              <span className="mini-stat-number">{stats.past}</span>
              <span className="mini-stat-label">Past</span>
            </div>
          </div>
        </div>
        
        <div className="header-actions">
          <button 
            className="btn-secondary"
            onClick={() => navigate('/curator/submission-template')}
            title="Manage submission template"
          >
            <Settings size={16} />
            Manage Template
          </button>
          
          <button 
            className="create-submission-btn"
            onClick={() => navigate('/curator/submissions/new')}
          >
            <Plus size={16} />
            Create New Submission
          </button>
          
          <div className="view-toggle">
            <button 
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <Grid size={16} />
            </button>
            <button 
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Section - REMOVED, now in header */}

      {/* Filters */}
      <div className="submissions-filters">
        <div className="search-bar">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search submissions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <Filter size={18} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="past">Past</option>
            <option value="no-deadline">No Deadline</option>
          </select>
        </div>
        
        <button onClick={loadCuratorSubmissions} className="refresh-button">
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Submissions Content */}
      {submissions.length === 0 ? (
        <div className="no-submissions">
          <DollarSign size={48} />
          <h3>No submissions found</h3>
          <p>No submission opportunities found for your galleries.</p>
        </div>
      ) : (
        <div className={`submissions-container ${viewMode}`}>
          {submissions.map((submission) => (
            <div 
              key={submission.submission_id}
              className="submission-card"
            >
              {/* Title */}
              <h3 className="submission-title">{submission.title || 'Untitled Submission'}</h3>

              {/* Status + Featured Labels */}
              <div className="header-labels">
                {submission.featured && (
                  <span className="featured-banner">FEATURED</span>
                )}
                <span className={`submission-status ${getStatusColor(submission.status)}`}>
                  {getStatusLabel(submission.status)}
                </span>
              </div>

              {/* Quick Details */}
              <div className="card-details">
                <div className="detail-row">
                  <Calendar size={16} />
                  <span>{formatDate(submission.submission_deadline)}</span>
                </div>
                
                <div className="detail-row">
                  <DollarSign size={16} />
                  <span>{formatPrice(submission.submission_price)}</span>
                  {submission.member_free && submission.submission_price > 0 && (
                    <span className="member-free-badge">Free for Members</span>
                  )}
                </div>
                
                {submission.max_allowed && (
                  <div className="detail-row">
                    <Users size={16} />
                    <span>{submission.max_allowed} pieces max</span>
                  </div>
                )}
              </div>

              {/* Venue Info */}
              <div className="card-venue">
                <span className="venue-label">Venue: {submission.galleryName}</span>
              </div>

              {/* Action Buttons */}
              <div className="card-actions">
                <button 
                  className="btn-edit"
                  onClick={() => navigate(`/curator/collections/${submission.collection_id}/edit-submission`)}
                  title="Edit Submission"
                >
                  Edit
                </button>
                
                <a 
                  className="btn-view"
                  href={`https://share.vibrantcast.com/submission?submissionId=${submission.submission_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View Public Submission Page"
                >
                  <Eye size={14} />
                  View
                </a>
                
                <button
                  className="btn-copy"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(`https://share.vibrantcast.com/submission?submissionId=${submission.submission_id}`);
                    alert('Submission URL copied!');
                  }}
                  title="Copy Submission URL"
                >
                  📋
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CuratorSubmissions;
